import {
  Box,
  IconButton,
  Button,
  createStyles,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  makeStyles,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Theme,
  useMediaQuery,
  Divider,
  Link,
} from '@material-ui/core';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import ErrorIcon from '@material-ui/icons/Error';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';

import { Payment, MutationStatusCode } from '../gql-types.generated';
import NumberFormatField from './NumberFormatField';
import { selectRefundErrorMessage, fetchRefundErrorMessage } from '../features/home/HomeSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dataBoxes: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
    },
    dataLabel: {
      color: 'grey',
    },
    dataValue: {
      color: 'black',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    dataGridItem: {
      padding: '10px !important',
    },
    dialogActions: {
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    dialogContent: {
      padding: 0,
    },
    dialogContextTitle: {
      borderBottom: '1px solid lightgrey',
      paddingLeft: 24,
      paddingBottom: 16,
      color: 'darkslategrey',
      marginBottom: 0,
    },
    dialogContextError: {
      borderBottom: '1px solid lightgrey',
      padding: '8px 24px',
      backgroundColor: '#ffe7e6',
      color: theme.palette.error.main,
      fontWeight: 500,
      display: 'flex',
    },
    dialogContextErrorIcon: {
      marginRight: 4,
    },
    dialogContextErrorHidden: {
      display: 'none',
    },
    dialogTitle: {
      paddingBottom: 6,
    },
    dialogButtons: {
      width: '100%',
    },
    gridItemsHorizontal: {
      width: '50%',
      margin: 0,
      padding: '12px 24px',
    },
    gridItemsHorizontalLeft: {
      width: '50%',
      borderRight: '1px solid lightgrey',
      margin: 0,
      padding: '12px 24px',
    },
    gridItemsVertical: {
      margin: 0,
      padding: '12px',
    },
    inlineBox: {
      display: 'flex',
    },
    textfield: {
      display: 'flex',
      marginBottom: 12,
      '&.textfieldError .MuiOutlinedInput-notchedOutline': {
        border: '1px solid red',
      },
    },
    radios: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: 2,
      color: theme.palette.grey[500],
    },
    paymentLink: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  }),
);

interface ErrorObj {
  class: string;
  message: string;
}

interface IssueRefundPaymentDialogProps {
  refundStatus?: string;
  paymentRecord?: Payment;
  defaultCurrency?: string | null;
  close: () => void;
  sendRefund: (paymentId: string, refundReason: string, amount: number | null) => void;
  clearRefundStateStatus: () => void;
  submit: () => void;
  openPaymentDetails: (paymentRecord: Payment) => void;
  isPaymentRefunded: boolean;
  paymentRefundedAmount?: number;
}

const IssueRefundPayment: React.FC<IssueRefundPaymentDialogProps> = (props: IssueRefundPaymentDialogProps) => {
  const classes = useStyles();
  const {
    close,
    paymentRecord,
    refundStatus,
    defaultCurrency,
    sendRefund,
    clearRefundStateStatus,
    submit,
    openPaymentDetails,
    isPaymentRefunded,
    paymentRefundedAmount,
  } = props;
  const amountRef = useRef<HTMLDivElement>(null);
  const reasonRef = useRef<HTMLDivElement>(null);
  const refundErrorMessage = useSelector(selectRefundErrorMessage);
  const dispatch = useDispatch();
  const getMaxRefundAmount = () => {
    // Calculate the maximum amount allowed for this refund using the payment amount minus the refunded amount.
    // The result is in cents.
    return paymentRecord?.amount && isPaymentRefunded && paymentRefundedAmount
      ? paymentRecord.amount - paymentRefundedAmount
      : paymentRecord?.amount;
  };
  const [userEnteredAmount, setUserEnteredAmount] = useState(() => {
    const remainingPaymentAmount = getMaxRefundAmount();
    return typeof remainingPaymentAmount === 'number' ? (remainingPaymentAmount / 100).toString() : '';
  });
  const [fullRefund, setFullRefund] = useState(isPaymentRefunded ? 'partial' : 'full');
  const [userEnteredReason, setUserEnteredReason] = useState('');
  const [disableAmountField, setDisableAmountField] = useState(
    //Sets this to true if the payment has not been partially refunded
    !isPaymentRefunded,
  );
  const [disableRequestSelection] = useState(
    //Sets this to true if the payment has been partially refunded
    isPaymentRefunded,
  );
  const [disableRefundButton, setDisableRefundButton] = useState(false);
  const [errorState, setErrorState] = useState<ErrorObj>({
    class: classes.dialogContextErrorHidden,
    message: 'Error',
  });
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const handleUserEnteredAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserEnteredAmount(event.target.value);
  };
  const handleUserEnteredReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserEnteredReason(event.target.value);
  };
  const handleRefundTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement;
    setFullRefund(value);
    if (value === 'full') {
      // set userAmount to amount
      // diable amount textbox
      setDisableAmountField(true);
      if (paymentRecord?.amount) {
        setUserEnteredAmount((paymentRecord.amount / 100).toString());
      }
    } else {
      // enable amount textbox
      setDisableAmountField(false);
    }
  };
  const handleSubmitClick = () => {
    setDisableAmountField(true);
    setDisableRefundButton(false);
    if (paymentRecord?.amount) {
      setUserEnteredAmount((paymentRecord.amount / 100).toString());
    }
    setFullRefund('full');
    setUserEnteredReason('');
    submit();
  };
  useEffect(() => {
    if (refundStatus === MutationStatusCode.Pending || refundStatus === MutationStatusCode.Success) {
      clearRefundStateStatus();
      handleSubmitClick();
    }
  }, [refundStatus]);

  const setError = (errorMessage: string) => {
    setErrorState({
      class: classes.dialogContextError,
      message: errorMessage,
    });
  };
  const removeError = () => {
    setErrorState({
      class: classes.dialogContextErrorHidden,
      message: '',
    });
    if (amountRef && amountRef.current) {
      amountRef.current.classList.remove('textfieldError');
    }
    if (reasonRef && reasonRef.current) {
      reasonRef.current.classList.remove('textfieldError');
    }

    if (refundErrorMessage !== '') {
      dispatch(fetchRefundErrorMessage(''));
    }
  };

  useEffect(() => {
    if (refundErrorMessage && refundErrorMessage !== '') {
      setError(refundErrorMessage);
    }
  }, [refundErrorMessage]);

  const handleCancelClick = () => {
    setDisableAmountField(true);
    setDisableRefundButton(false);
    if (paymentRecord?.amount) {
      setUserEnteredAmount((paymentRecord.amount / 100).toString());
    }
    setFullRefund('full');
    setUserEnteredReason('');
    removeError();
    close();
  };
  const checkFieldsForSubmit = (): boolean => {
    let ret = true;
    let errorText = '';
    if (userEnteredReason.trim() === '') {
      ret = false;
      errorText = 'Must include reason for refund.';
      //Add Error highlight
      if (reasonRef && reasonRef.current) {
        reasonRef.current.classList.add('textfieldError');
      }
    }
    if (paymentRecord?.amount) {
      const maxRefundAmount = getMaxRefundAmount() ?? 0;
      if (fullRefund !== 'full') {
        if (userEnteredAmount !== '') {
          const submitAmount = Math.round(parseFloat(userEnteredAmount) * 100);
          if (submitAmount > maxRefundAmount) {
            ret = false;
            if (maxRefundAmount < paymentRecord?.amount) {
              errorText =
                errorText === '' ? 'Cannot refund more than the amount remaining' : 'Please fill in all fields with valid data.';
            } else {
              errorText = errorText === '' ? 'Cannot refund more than the amount paid' : 'Please fill in all fields with valid data.';
            }
            //Add Error highlight
            if (amountRef && amountRef.current) {
              amountRef.current.classList.add('textfieldError');
            }
          }
          if (submitAmount < 100) {
            ret = false;
            errorText = errorText === '' ? 'You may not refund less than one dollar' : 'Please fill in all fields with valid data.';
            //Add Error highlight
            if (amountRef && amountRef.current) {
              amountRef.current.classList.add('textfieldError');
            }
          }
        } else {
          ret = false;
          errorText = errorText === '' ? 'Must include amount to refund' : 'Please fill in all fields with valid data.';
          //Add Error highlight
          if (amountRef && amountRef.current) {
            amountRef.current.classList.add('textfieldError');
          }
        }
      }
    }
    if (errorText !== '') {
      setError(errorText);
    }
    return ret;
  };
  const submitFullRefund = () => {
    if (paymentRecord?.id) {
      setDisableRefundButton(true);
      sendRefund(paymentRecord.id, userEnteredReason.trim(), null);
    }
  };
  const submitPartialRefund = () => {
    if (paymentRecord?.id) {
      setDisableRefundButton(true);
      sendRefund(paymentRecord.id, userEnteredReason.trim(), Math.round(parseFloat(userEnteredAmount) * 100));
    }
  };
  const handleRefundClick = () => {
    removeError();
    const fieldCheckPassed = checkFieldsForSubmit();
    if (fieldCheckPassed) {
      if (fullRefund === 'full') {
        submitFullRefund();
      } else {
        submitPartialRefund();
      }
    }
  };
  if (paymentRecord) {
    return (
      <Box>
        <DialogTitle className={classes.dialogTitle} data-cy="refund-dialog-title">
          Issue a refund
        </DialogTitle>
        <IconButton
          aria-label="close"
          title="close"
          className={classes.closeButton}
          onClick={handleCancelClick}
          data-cy="refund-modal-close"
        >
          <CloseIcon />
        </IconButton>
        <DialogContentText className={classes.dialogContextTitle}>Please confirm refund amount and reason.</DialogContentText>
        <DialogContentText className={errorState.class} data-cy="refund-error-message">
          <ErrorIcon className={classes.dialogContextErrorIcon} />
          {errorState.message}
        </DialogContentText>
        <DialogContent className={classes.dialogContent}>
          <Grid container className={classes.inlineBox}>
            <Grid
              container
              item
              xs={matches ? 12 : undefined}
              direction="row"
              spacing={3}
              alignItems="stretch"
              className={matches ? classes.gridItemsVertical : classes.gridItemsHorizontalLeft}
            >
              <Grid item xs={5} className={classes.dataGridItem}>
                <Typography className={classes.dataLabel}>Payment ID</Typography>
              </Grid>
              <Grid item xs={7} className={classes.dataGridItem}>
                <Link
                  href="#"
                  onClick={() => {
                    openPaymentDetails(paymentRecord);
                  }}
                >
                  <Typography className={classes.paymentLink}>***{paymentRecord.id.slice(-9)}</Typography>
                </Link>
              </Grid>
              <Grid item xs={5} className={classes.dataGridItem}>
                <Typography className={classes.dataLabel}>Payer Contact</Typography>
              </Grid>
              <Grid item xs={7} className={classes.dataGridItem}>
                <Typography className={classes.dataValue}>{paymentRecord.initiatedBy ?? '- -'}</Typography>
              </Grid>
              <Grid item xs={5} className={classes.dataGridItem}>
                <Typography className={classes.dataLabel}>Completed On</Typography>
              </Grid>
              <Grid item xs={7} className={classes.dataGridItem}>
                <Typography className={classes.dataValue}>
                  {new Date(paymentRecord.completedTimestamp as string).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={5} className={classes.dataGridItem}>
                <Typography className={classes.dataLabel}>Amount</Typography>
              </Grid>
              <Grid item xs={7} className={classes.dataGridItem}>
                <Typography className={classes.dataValue} data-cy="requested-amount">
                  {typeof paymentRecord.amount === 'number'
                    ? new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: defaultCurrency || 'USD',
                        currencyDisplay: 'symbol',
                      }).format(paymentRecord.amount / 100)
                    : ''}
                </Typography>
              </Grid>
              <Grid item xs={5} className={classes.dataGridItem}>
                <Typography className={classes.dataLabel}>Status</Typography>
              </Grid>
              <Grid item xs={7} className={classes.dataGridItem}>
                <Typography className={classes.dataValue}>
                  {paymentRecord.status &&
                    paymentRecord.status
                      .split(' ')
                      .map(w => w[0].toUpperCase() + w.substr(1).toLowerCase()) //Mix cases the status
                      .join(' ')}
                </Typography>
              </Grid>
            </Grid>
            {matches && (
              <Grid item xs={12} style={{ width: 1 }}>
                <Divider orientation={'horizontal'} />
              </Grid>
            )}
            <Grid item xs={matches ? 12 : undefined} className={matches ? classes.gridItemsVertical : classes.gridItemsHorizontal}>
              <Box>
                <RadioGroup
                  aria-label="request type"
                  name="requestSelection"
                  className={classes.radios}
                  value={fullRefund}
                  onChange={handleRefundTypeChange}
                >
                  <FormControlLabel
                    value="full"
                    control={<Radio color="primary" data-cy="full-refund" />}
                    label="Full refund"
                    disabled={disableRequestSelection}
                  />
                  <FormControlLabel
                    value="partial"
                    control={<Radio color="primary" data-cy="partial-refund" />}
                    label="Partial refund"
                    disabled={disableRequestSelection}
                  />
                </RadioGroup>
                <Box>
                  <TextField
                    variant="outlined"
                    label="Amount"
                    className={classes.textfield}
                    value={userEnteredAmount}
                    onChange={handleUserEnteredAmountChange}
                    InputProps={{
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      inputComponent: NumberFormatField as any,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                    disabled={disableAmountField}
                    ref={amountRef}
                    data-cy="refund-amount"
                  ></TextField>
                  <TextField
                    variant="outlined"
                    label="Refund Reason"
                    className={classes.textfield}
                    value={userEnteredReason}
                    onChange={handleUserEnteredReasonChange}
                    ref={reasonRef}
                    data-cy="refund-reason"
                  ></TextField>
                </Box>
                <Grid className={matches ? '' : classes.dialogActions} container spacing={1} justify="flex-end">
                  <Grid item xs={3}>
                    <Button className={classes.dialogButtons} color="primary" onClick={handleCancelClick} data-cy="cancel-refund">
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={3}>
                    <Button
                      className={classes.dialogButtons}
                      color="primary"
                      variant="contained"
                      onClick={handleRefundClick}
                      disabled={disableRefundButton}
                      data-cy="process-refund"
                    >
                      Refund
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Box>
    );
  }
  submit();
  return null;
};

export default IssueRefundPayment;
